import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Highlighter, Trash2 } from 'lucide-react';
import type { Highlight } from '@grove/api-client';
import { api } from '../lib/api';

interface HighlightsPanelProps {
  articleId: string;
  highlights: Highlight[];
  /** Filed articles are released — Grove shows its copy read-only. */
  readOnly?: boolean;
}

/**
 * What you kept from an article, and what you thought about it.
 *
 * Highlights were write-only until now: you could select text and it vanished
 * into the database, surfacing only in the vault after a sync. The note on
 * each one is the piece that makes a highlight worth keeping — it's the
 * difference between a clipping and a thought.
 */
export function HighlightsPanel({ articleId, highlights, readOnly }: HighlightsPanelProps) {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['article', articleId] });
    queryClient.invalidateQueries({ queryKey: ['highlights'] });
  };

  const saveNote = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => api.updateHighlight(id, { note }),
    onSuccess: () => {
      setEditingId(null);
      setDraft('');
      refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteHighlight(id),
    onSuccess: refresh,
  });

  if (!highlights.length) {
    return (
      <section className="mt-12 pt-8 border-t border-border-subtle">
        <h2 className="text-sm font-semibold text-text-primary inline-flex items-center gap-2">
          <Highlighter size={15} className="text-prism-violet" /> Highlights
        </h2>
        <p className="text-xs mt-2 text-text-muted">
          {readOnly
            ? 'Nothing was highlighted before this was filed.'
            : 'Select any passage to keep it. You can add a note to each one.'}
        </p>
      </section>
    );
  }

  return (
    <section className="mt-12 pt-8 border-t border-border-subtle">
      <h2 className="text-sm font-semibold text-text-primary inline-flex items-center gap-2 mb-4">
        <Highlighter size={15} className="text-prism-violet" /> Highlights
        <span className="text-xs font-normal text-text-muted">({highlights.length})</span>
      </h2>

      <ul className="space-y-4">
        {highlights.map((h) => (
          <li key={h.id} className="border-l-2 border-prism-violet/40 pl-4">
            <p className="text-sm text-text-secondary italic">“{h.text}”</p>

            {h.addedBy && (
              <p className="text-xs mt-1 text-text-muted">highlighted by {h.addedBy}</p>
            )}

            {editingId === h.id ? (
              <div className="mt-2">
                <textarea
                  autoFocus
                  rows={3}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="What do you make of this?"
                  className="w-full px-3 py-2 rounded-lg text-sm text-text-primary bg-surface-0 border border-border-subtle focus:border-prism-violet focus:outline-none"
                />
                <div className="flex gap-3 mt-1.5">
                  <button
                    onClick={() => saveNote.mutate({ id: h.id, note: draft })}
                    disabled={saveNote.isPending}
                    className="text-xs font-medium text-violet-400 hover:text-violet-300 transition-colors"
                  >
                    {saveNote.isPending ? 'Saving…' : 'Save note'}
                  </button>
                  <button
                    onClick={() => setEditingId(null)}
                    className="text-xs text-text-muted hover:text-text-secondary transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-1.5 flex items-start gap-3">
                {h.note ? (
                  <p className="text-sm flex-1 text-text-primary">{h.note}</p>
                ) : (
                  <span className="text-xs flex-1 text-text-muted">No note</span>
                )}
                {!readOnly && (
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <button
                      onClick={() => {
                        setEditingId(h.id);
                        setDraft(h.note ?? '');
                      }}
                      className="text-xs text-violet-400 hover:text-violet-300 transition-colors"
                    >
                      {h.note ? 'edit note' : '+ add note'}
                    </button>
                    <button
                      onClick={() => remove.mutate(h.id)}
                      aria-label="Delete highlight"
                      className="text-text-muted hover:text-red-400 transition-colors"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
